// Пароль как второй способ входа. SMY-121
//
// Живёт в общем ядре по той же причине, что и проверка номера: форма гасит
// кнопку по этим правилам, а сервер обязан проверить ровно то же ещё раз.
// Две реализации одного правила разойдутся молча — и разойдутся в сторону
// «клиент пустил, сервер отказал», то есть в тупик без объяснения.
//
// Пароль здесь ВТОРИЧЕН и это не оговорка, а устройство входа: завести
// аккаунт можно только по номеру, потому что номер подтверждается SMS, а
// пароль — ничем. Пароль лишь избавляет вошедшего от ожидания SMS, и потому
// он необязателен, снимаем и заводится уже внутри кабинета.

/**
 * Восемь знаков — не «шесть, как у кода».
 *
 * Код входа живёт минуты и умирает после пяти попыток, поэтому шести цифр
 * ему хватает. Пароль живёт до смены и проверяется столько раз, сколько
 * попыток допустит сервер, — короткий пароль здесь уже не защита.
 */
export const PASSWORD_MIN_LENGTH = 8

/**
 * Потолок нужен не ради безопасности, а ради предсказуемости: scrypt честно
 * считает хеш от любой длины, и мегабайтная строка в поле пароля — это
 * отказ в обслуживании ценой одного запроса.
 */
export const PASSWORD_MAX_LENGTH = 128

export type PasswordProblem = 'too_short' | 'too_long' | 'only_digits' | 'same_as_login'

/**
 * Что не так с паролем. null — всё в порядке.
 *
 * Проверок намеренно три, и ни одна не про «заглавные буквы и спецсимвол»:
 * требования к составу гонят человека в «Password1!» и не прибавляют
 * стойкости. Отсекаем ровно то, что ломает вход на самом деле, — короткое,
 * цифровое (то есть тот же код, только длиннее) и собственный номер.
 */
/**
 * @param login чем человек входит — почта (SMY-167), раньше цифры номера.
 */
export function checkPassword(password: string, login?: string): PasswordProblem | null {
  if (password.length < PASSWORD_MIN_LENGTH) return 'too_short'
  if (password.length > PASSWORD_MAX_LENGTH) return 'too_long'
  // Пароль из одних цифр перебирается на порядки быстрее и, что важнее,
  // человек ставит сюда дату рождения или тот же код из письма.
  if (/^\d+$/.test(password)) return 'only_digits'
  if (login != null && login !== '' && containsLogin(password, login)) return 'same_as_login'
  return null
}

/**
 * Содержит ли пароль свой логин.
 *
 * ⚠️ У ПОЧТЫ СРАВНИВАЕТСЯ ИМЯ ДО СОБАКИ, А НЕ ВЕСЬ АДРЕС, и это не упрощение.
 * Целиком адрес в пароль не влезает почти никогда — правило, проверяющее
 * `password.includes('ivan@gmail.com')`, не срабатывало бы ни разу и было бы
 * проверкой на бумаге. А вот `ivan2024` при логине `ivan@gmail.com` — ровно
 * тот пароль, ради которого правило написано.
 *
 * Регистр гасится: `Ivan2024` не безопаснее `ivan2024`.
 *
 * Короткое имя (два знака и меньше) не проверяется вовсе: `an` найдётся в
 * половине нормальных паролей, и правило превратилось бы в запрет случайных
 * букв — то есть в то, что человек обходит, дописывая цифру, а не думая.
 */
function containsLogin(password: string, login: string): boolean {
  const at = login.lastIndexOf('@')
  const name = (at > 0 ? login.slice(0, at) : login).toLowerCase()
  if (name.length <= 2) return false
  return password.toLowerCase().includes(name)
}

/** Тексты отказов. Один источник: их печатают и сервер, и форма. */
export const PASSWORD_PROBLEM_TEXT: Record<PasswordProblem, string> = {
  too_short: `Пароль короче ${PASSWORD_MIN_LENGTH} знаков`,
  too_long: `Пароль длиннее ${PASSWORD_MAX_LENGTH} знаков`,
  only_digits: 'Пароль из одних цифр слишком просто подобрать — добавь буквы',
  same_as_login: 'Пароль не должен повторять твою почту',
}

export const isValidPassword = (password: string, login?: string): boolean =>
  checkPassword(password, login) == null

/**
 * Чем пароль полезен и что будет, если его забыть. Экраны 01-после-входа и 11.
 *
 * Один текст на четыре места: предложение после первого входа и окно «Завести
 * пароль» в профиле, и то и другое на двух платформах. Четыре копии одного
 * абзаца расходятся не сразу — расходится та, которую правят, — и человек
 * получает два разных обещания об одном и том же.
 *
 * Обе фразы отвечают на страх, а не описывают устройство: первая — зачем это
 * вообще, вторая — что будет, если забуду. Второй вопрос человек задаёт себе
 * ровно в тот момент, когда придумывает пароль, и не ответить на него значит
 * оставить его закрывать окно.
 */
export const PASSWORD_OFFER_TEXT =
  'Ты можешь поставить пароль, чтобы не ожидать SMS при каждом входе. ' +
  'Если вдруг забудешь пароль, то сможешь восстановить его через SMS код.'
